import { greatCircleArc, TIER_COLOR, type LatLng, type Tier } from '@geosport/shared';
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import type * as MapLibreNS from 'maplibre-gl';
import type { GeoJSONSource, Map as MapInstance, Marker } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { findRegion } from '../lib/regions';
import { buildStyle, type MapStyleKind } from './style';

type MapLibreModule = typeof MapLibreNS;

export interface RevealOverlay {
  guess: LatLng;
  answer: LatLng & { place: string; year: number };
  tier: Tier;
}

export interface GlobeProps {
  pin: LatLng | null;
  onPick?: (p: LatLng) => void;
  reveal: RevealOverlay | null;
  interactive: boolean;
  reducedMotion: boolean;
  mapStyle: MapStyleKind;
  /** Fraction of the container hidden by the bottom sheet during a reveal (framing padding). */
  bottomInset?: number;
  className?: string;
}

// Empty env values (e.g. an unset CI variable) fall back too, hence || rather than ??.
const TILES_URL = import.meta.env.VITE_TILES_URL || '/tiles/v6/{z}/{x}/{y}.pbf';
const TILES_MAXZOOM = Number(import.meta.env.VITE_TILES_MAXZOOM || 6);
const GLYPHS_URL =
  import.meta.env.VITE_GLYPHS_URL ||
  'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf';

const WORLD_VIEW = { center: [10, 20] as [number, number], zoom: 1.3 };
const ARC_MS = 1100; // ≤ 1.2 s (PRD §10)
const MAX_ZOOM = 12;

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

const point = (p: LatLng): FeatureCollection => ({
  type: 'FeatureCollection',
  features: [
    { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } },
  ],
});

const line = (coords: [number, number][]): FeatureCollection => ({
  type: 'FeatureCollection',
  features:
    coords.length < 2
      ? []
      : [
          {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: coords },
          },
        ],
});

const polygon = (geometry: Polygon | MultiPolygon): FeatureCollection => ({
  type: 'FeatureCollection',
  features: [{ type: 'Feature', properties: {}, geometry }],
});

let protocolReady = false;

/**
 * `gz://` protocol: fetch a tile and gunzip it client-side when the bytes are gzip, whatever the
 * server says. Static-asset hosts do not reliably preserve Content-Encoding for pre-compressed
 * files, and this keeps the unpacked fallback tiles working everywhere.
 */
async function fetchTile(url: string, signal: AbortSignal): Promise<{ data: ArrayBuffer }> {
  const res = await fetch(url, { signal });
  if (res.status === 404 || res.status === 204) return { data: new ArrayBuffer(0) };
  if (!res.ok) throw new Error(`Tile request failed: ${res.status}`);
  const raw = await res.arrayBuffer();
  const bytes = new Uint8Array(raw);
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
    const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('gzip'));
    return { data: await new Response(stream).arrayBuffer() };
  }
  return { data: raw };
}

async function loadMapLibre(): Promise<MapLibreModule> {
  // MapLibre 6 resolves its worker relative to import.meta.url, which breaks under bundlers.
  // Let Vite bundle the worker and hand MapLibre the resulting URL instead.
  const [ml, pm, worker] = await Promise.all([
    import('maplibre-gl'),
    import('pmtiles'),
    import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'),
  ]);
  await import('maplibre-gl/dist/maplibre-gl.css');
  if (!protocolReady) {
    ml.setWorkerUrl(worker.default);
    const protocol = new pm.Protocol();
    ml.addProtocol('pmtiles', protocol.tile);
    ml.addProtocol('gz', (params, abort) =>
      fetchTile(params.url.replace(/^gz:\/\//, ''), abort.signal),
    );
    protocolReady = true;
  }
  return ml;
}

/** Teardrop pin as an SVG element (guess = amber, answer = green). */
function pinElement(color: string, label: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'geo-pin';
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', label);
  el.innerHTML = `<svg viewBox="0 0 30 40" width="30" height="40" aria-hidden="true">
    <path d="M15 1C7.3 1 1 7.2 1 14.8 1 25 15 39 15 39s14-14 14-24.2C29 7.2 22.7 1 15 1z" fill="${color}" stroke="#0b1020" stroke-width="2"/>
    <circle cx="15" cy="14.5" r="5.5" fill="#0b1020"/>
  </svg>`;
  return el;
}

/** Game sources/layers, re-added whenever the base style changes. */
function addGameLayers(map: MapInstance): void {
  if (map.getSource('arc')) return;
  map.addSource('arc', { type: 'geojson', data: EMPTY });
  map.addSource('region', { type: 'geojson', data: EMPTY });
  map.addSource('answer', { type: 'geojson', data: EMPTY });
  map.addLayer({
    id: 'region-fill',
    type: 'fill',
    source: 'region',
    paint: { 'fill-color': '#22c55e', 'fill-opacity': 0.28 },
  });
  map.addLayer({
    id: 'region-line',
    type: 'line',
    source: 'region',
    paint: { 'line-color': '#4ade80', 'line-width': 2.5, 'line-opacity': 0.95 },
  });
  map.addLayer({
    id: 'arc-casing',
    type: 'line',
    source: 'arc',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': 'rgba(0,0,0,0.6)', 'line-width': 5 },
  });
  map.addLayer({
    id: 'arc',
    type: 'line',
    source: 'arc',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#ffffff', 'line-width': 2.5, 'line-opacity': 0.95 },
  });
  map.addLayer({
    id: 'answer-halo',
    type: 'circle',
    source: 'answer',
    paint: { 'circle-radius': 18, 'circle-color': '#22c55e', 'circle-opacity': 0.25 },
  });
}

/**
 * Lazy-loaded MapLibre globe (PRD §6.2). Drag to rotate, pinch/scroll to zoom, tap to drop a
 * draggable pin. The reveal highlights the answer's country or state, draws an animated
 * great-circle arc coloured by tier, and frames both points above the bottom sheet.
 */
export function Globe({
  pin,
  onPick,
  reveal,
  interactive,
  reducedMotion,
  mapStyle,
  bottomInset = 0.55,
  className,
}: GlobeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapInstance | null>(null);
  const mlRef = useRef<MapLibreModule | null>(null);
  const pinMarkerRef = useRef<Marker | null>(null);
  const answerMarkerRef = useRef<Marker | null>(null);
  const labelMarkerRef = useRef<Marker | null>(null);
  const animRef = useRef<number | null>(null);
  const onPickRef = useRef(onPick);
  const interactiveRef = useRef(interactive);
  const styleRef = useRef(mapStyle);
  const [ready, setReady] = useState(false);
  const [styleTick, setStyleTick] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    onPickRef.current = onPick;
    interactiveRef.current = interactive;
  }, [onPick, interactive]);

  // Create the map once.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;
    let map: MapInstance | null = null;

    void loadMapLibre()
      .then((ml) => {
        if (cancelled) return;
        mlRef.current = ml;
        map = new ml.Map({
          container,
          style: buildStyle(styleRef.current, TILES_URL, GLYPHS_URL, TILES_MAXZOOM),
          center: WORLD_VIEW.center,
          zoom: WORLD_VIEW.zoom,
          minZoom: 0.8,
          maxZoom: MAX_ZOOM,
          maxPitch: 0,
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false,
          attributionControl: false,
        });
        mapRef.current = map;
        // The daily video recorder (scripts/src/daily-video.ts) steers the camera in ?record mode.
        if (new URLSearchParams(window.location.search).has('record')) {
          (window as unknown as { __gsMap?: MapInstance }).__gsMap = map;
        }
        map.addControl(new ml.AttributionControl({ compact: true }), 'bottom-right');
        map.touchZoomRotate.disableRotation();

        let loggedTileError = false;
        map.on('error', (e) => {
          if (!loggedTileError) {
            loggedTileError = true;
            console.warn('[globe] map error (tiles missing?)', e.error?.message ?? e);
          }
        });

        map.on('click', (e) => {
          if (!interactiveRef.current) return;
          onPickRef.current?.({ lat: e.lngLat.lat, lng: e.lngLat.lng });
        });

        const readyTimer = window.setTimeout(() => setReady(true), 8000);
        map.once('idle', () => {
          window.clearTimeout(readyTimer);
          setReady(true);
        });
        // Fires on the first load and after every setStyle().
        map.on('style.load', () => {
          if (!map) return;
          addGameLayers(map);
          setReady(true);
          setStyleTick((t) => t + 1);
        });
      })
      .catch((err: unknown) => {
        console.error('[globe] failed to start', err);
        setFailed(true);
      });

    return () => {
      cancelled = true;
      if (animRef.current !== null) cancelAnimationFrame(animRef.current);
      pinMarkerRef.current?.remove();
      answerMarkerRef.current?.remove();
      labelMarkerRef.current?.remove();
      pinMarkerRef.current = null;
      answerMarkerRef.current = null;
      labelMarkerRef.current = null;
      map?.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  // Base style switch (satellite ↔ dark); game layers are re-added on style.load.
  useEffect(() => {
    const map = mapRef.current;
    if (styleRef.current === mapStyle) return;
    styleRef.current = mapStyle;
    if (!map || !ready) return;
    map.setStyle(buildStyle(mapStyle, TILES_URL, GLYPHS_URL, TILES_MAXZOOM));
  }, [mapStyle, ready]);

  // Guess pin: a draggable marker.
  useEffect(() => {
    const map = mapRef.current;
    const ml = mlRef.current;
    if (!map || !ml || !ready) return;
    if (!pin) {
      pinMarkerRef.current?.remove();
      pinMarkerRef.current = null;
      return;
    }
    if (!pinMarkerRef.current) {
      const el = pinElement('#f5b301', 'Your guess (drag to adjust)');
      el.classList.add('is-new');
      const marker = new ml.Marker({ element: el, anchor: 'bottom', draggable: true })
        .setLngLat([pin.lng, pin.lat])
        .addTo(map);
      marker.on('dragstart', () => el.classList.add('is-dragging'));
      marker.on('dragend', () => {
        el.classList.remove('is-dragging');
        const ll = marker.getLngLat();
        onPickRef.current?.({ lat: ll.lat, lng: ll.lng });
      });
      window.setTimeout(() => el.classList.remove('is-new'), 1300);
      pinMarkerRef.current = marker;
    } else {
      pinMarkerRef.current.setLngLat([pin.lng, pin.lat]);
    }
    pinMarkerRef.current.setDraggable(interactive);
  }, [pin, ready, interactive, styleTick]);

  // Cursor.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.getCanvas().style.cursor = interactive ? 'crosshair' : '';
  }, [interactive, ready]);

  // Reveal: region highlight, answer pin + label, animated arc, camera framing.
  useEffect(() => {
    const map = mapRef.current;
    const ml = mlRef.current;
    if (!map || !ml || !ready) return;
    const arcSrc = map.getSource('arc') as GeoJSONSource | undefined;
    const ansSrc = map.getSource('answer') as GeoJSONSource | undefined;
    const regionSrc = map.getSource('region') as GeoJSONSource | undefined;
    if (animRef.current !== null) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
    answerMarkerRef.current?.remove();
    labelMarkerRef.current?.remove();
    answerMarkerRef.current = null;
    labelMarkerRef.current = null;

    if (!reveal) {
      arcSrc?.setData(EMPTY);
      ansSrc?.setData(EMPTY);
      regionSrc?.setData(EMPTY);
      map.easeTo({ ...WORLD_VIEW, duration: reducedMotion ? 0 : 700 });
      return;
    }

    let stale = false;
    const color = TIER_COLOR[reveal.tier];
    if (map.getLayer('arc')) map.setPaintProperty('arc', 'line-color', color);
    if (map.getLayer('answer-halo')) map.setPaintProperty('answer-halo', 'circle-color', color);
    if (map.getLayer('region-fill')) map.setPaintProperty('region-fill', 'fill-color', color);
    if (map.getLayer('region-line')) map.setPaintProperty('region-line', 'line-color', color);

    const arc = greatCircleArc(reveal.guess, reveal.answer, 64);
    ansSrc?.setData(point(reveal.answer));
    regionSrc?.setData(EMPTY);
    void findRegion(reveal.answer).then((region) => {
      if (stale || !region) return;
      (map.getSource('region') as GeoJSONSource | undefined)?.setData(polygon(region.geometry));
    });

    answerMarkerRef.current = new ml.Marker({
      element: pinElement('#22c55e', `Answer: ${reveal.answer.place}`),
      anchor: 'bottom',
    })
      .setLngLat([reveal.answer.lng, reveal.answer.lat])
      .addTo(map);

    const el = document.createElement('div');
    el.className =
      'pointer-events-none rounded-lg bg-ink/90 px-2 py-1 text-xs font-semibold text-white shadow-lg ring-1 ring-white/20 whitespace-nowrap';
    el.textContent = `${reveal.answer.place} · ${reveal.answer.year}`;
    labelMarkerRef.current = new ml.Marker({ element: el, anchor: 'bottom', offset: [0, -44] })
      .setLngLat([reveal.answer.lng, reveal.answer.lat])
      .addTo(map);

    const first = arc[0] ?? [reveal.guess.lng, reveal.guess.lat];
    const bounds = arc.reduce((b, p) => b.extend(p), new ml.LngLatBounds(first, first));
    const h = map.getContainer().clientHeight;
    map.fitBounds(bounds, {
      padding: { top: 180, bottom: Math.round(h * bottomInset) + 30, left: 40, right: 40 },
      maxZoom: 7,
      duration: reducedMotion ? 0 : 900,
    });

    if (reducedMotion) {
      arcSrc?.setData(line(arc));
    } else {
      const start = performance.now();
      const step = (now: number): void => {
        const t = Math.min(1, (now - start) / ARC_MS);
        const eased = 1 - Math.pow(1 - t, 3);
        const count = Math.max(2, Math.ceil(eased * arc.length));
        arcSrc?.setData(line(arc.slice(0, count)));
        if (t < 1) animRef.current = requestAnimationFrame(step);
        else animRef.current = null;
      };
      animRef.current = requestAnimationFrame(step);
    }
    return () => {
      stale = true;
    };
  }, [reveal, ready, reducedMotion, bottomInset, styleTick]);

  const zoomBy = (delta: number): void => {
    const map = mapRef.current;
    if (!map) return;
    map.easeTo({ zoom: map.getZoom() + delta, duration: reducedMotion ? 0 : 300 });
  };

  return (
    <div className={className ?? 'relative h-full w-full'}>
      <div
        ref={containerRef}
        className={`h-full w-full ${mapStyle === 'satellite' ? 'bg-black' : 'bg-[#0a1a33]'}`}
        role="application"
        aria-label="World globe"
      />
      {ready && !failed && !reveal && (
        <div className="absolute top-1/2 right-3 z-10 flex -translate-y-1/2 flex-col gap-2">
          <button
            type="button"
            onClick={() => zoomBy(1)}
            aria-label="Zoom in"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-ink/80 text-xl font-bold text-white ring-1 ring-white/20 backdrop-blur hover:bg-ink-3"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => zoomBy(-1)}
            aria-label="Zoom out"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-ink/80 text-xl font-bold text-white ring-1 ring-white/20 backdrop-blur hover:bg-ink-3"
          >
            −
          </button>
        </div>
      )}
      {!ready && !failed && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-white/60">
          Loading globe…
        </div>
      )}
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-white/80">
          The globe could not start in this browser. Try updating your browser or enabling WebGL.
        </div>
      )}
    </div>
  );
}
